<script lang="ts">
  import { onDestroy } from "svelte";
  import { DateTime, type ToRelativeUnit } from "luxon";
  import type { ClassValue } from "svelte/elements";
  import Tooltip from "./Tooltip.svelte";

  interface Props {
    date: string;
    showAgo?: boolean;
    showTooltip?: boolean;
    style?: "long" | "short" | "narrow";
    autoLocale?: boolean;
    class?: ClassValue;
  }

  let {
    date,
    showAgo = true,
    showTooltip = true,
    style = "long",
    autoLocale = false,
    class: className,
    ...rest
  }: Props = $props();

  const unit: ToRelativeUnit[] = ["years", "months", "weeks", "days", "hours", "minutes"];

  // svelte-ignore state_referenced_locally
  const dateTime = DateTime.fromISO(date);
  const fullTimestamp = dateTime.toFormat("yyyy-MM-dd HH:mm:ss '(UTC' Z')'");

  // svelte-ignore state_referenced_locally
  let timeAgo = $state(
    dateTime.toRelative({
      style,
      unit,
      locale: autoLocale ? undefined : "en",
    }),
  );

  const updateTimeAgo = (): void => {
    timeAgo = dateTime.toRelative({
      style,
      unit,
      locale: autoLocale ? undefined : "en",
    });

    const englishDateTime = DateTime.fromISO(date).toRelative({ locale: "en" });

    if (!timeAgo || !englishDateTime) {
      clearInterval(interval);
      return;
    }

    let duration;

    // extend the interval duration as more time passes
    if (englishDateTime.includes("min") || englishDateTime.includes("sec")) {
      duration = 1000 * 60; // 1 minute
    } else if (englishDateTime.includes("hour")) {
      duration = 1000 * 60 * 60; // 1 hour
    } else {
      duration = 1000 * 60 * 60 * 24; // 1 day
    }

    clearInterval(interval);
    interval = setInterval(updateTimeAgo, duration);
  };

  let interval = setInterval(updateTimeAgo, 60000);

  onDestroy(() => {
    clearInterval(interval);
  });

  const formattedTimeAgo = $derived(
    timeAgo
      ?.replace(/^(\d+ sec|0 min?).*$/, "just now")
      .replace(/ ago$/, showAgo ? " ago" : "")
      .replace(".", ""),
  );
</script>

{#if timeAgo}
  {#if showTooltip}
    <Tooltip text={fullTimestamp} hideIcon copyable>
      <time class={className} datetime={date} {...rest}>
        {formattedTimeAgo}
      </time>
    </Tooltip>
  {:else}
    <time class={className} datetime={date} {...rest}>
      {formattedTimeAgo}
    </time>
  {/if}
{/if}
